import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { authService } from "../../services/auth";
import { subscriptionService } from "../../services/subscription";
import { getApiErrorMessage } from "../../utils/apiErrors";
import {
  LIMIT_ICONS,
  formatPrice,
  getPlanPriceDisplay,
  buildPlanFeatures,
  buildPlanLimits,
} from "./subscriptionShared";

const SubscriptionSelect = ({
  currentUser,
  embedded = false,
  returnPath,
  successPath,
  onSkip,
  showSkipLink = false,
  onCheckoutSuccess,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [refreshedUser, setRefreshedUser] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [planOrder, setPlanOrder] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const displayUser = refreshedUser || currentUser;
  const limits = displayUser?.limits || null;
  const planKey = limits?.key || "scout";
  const currentSubscription = displayUser?.subscription || null;
  const eligibleForTrial = displayUser?.trial?.eligible_for_trial_subscription ?? false;
  const isOnFreeTier = planKey === "scout";
  const isChangingPlan = Boolean(currentSubscription?.active && !isOnFreeTier);

  const checkoutReturnPath = returnPath || location.pathname;
  const checkoutSuccessPath = successPath || (embedded ? checkoutReturnPath : "/account/subscription/");

  const refreshUserConfig = useCallback((onFetched) => {
    authService.fetchUser(
      (response) => {
        if (response?.data) {
          setRefreshedUser(response.data);
          if (onFetched) onFetched(response.data);
        }
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    authService.fetchUser(
      (response) => {
        if (response?.data) {
          setRefreshedUser(response.data);
          const cycle = response.data?.subscription?.billing_cycle;
          if (cycle === "yearly" || cycle === "monthly") {
            setBillingCycle(cycle);
          }
        }
      },
      () => {}
    );
    subscriptionService.getPlanConfig(
      (data) => {
        setPlanConfig(data.plans);
        setPlanOrder(data.order);
        if (data.trial_days) setTrialDays(data.trial_days);
      },
      () => {
        setPlanConfig({});
        setPlanOrder([]);
      }
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get("success");
    const cancelled = params.get("checkout");
    if (!success && cancelled !== "cancelled") return;

    if (success === "true") {
      setSuccessMessage("Your subscription is live!");
    } else if (cancelled === "cancelled") {
      setError(null);
    }

    params.delete("success");
    params.delete("checkout");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );

    if (success !== "true") return;

    let attempts = 0;
    const pollUserConfig = () => {
      refreshUserConfig((data) => {
        if (data?.limits?.key === "scout" && attempts < 3) {
          attempts += 1;
          setTimeout(pollUserConfig, 2000);
          return;
        }
        if (onCheckoutSuccess) {
          onCheckoutSuccess();
        }
      });
    };

    setTimeout(pollUserConfig, 2000);
  }, [location.search, location.pathname, navigate, refreshUserConfig, onCheckoutSuccess]);

  const CheckIcon = ({ className = "text-theme-primary" }) => (
    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const startCheckout = (key) => {
    setError(null);
    setCheckoutPlan(key);
    subscriptionService.createCheckoutSession(
      key,
      billingCycle,
      { returnPath: checkoutReturnPath, successPath: checkoutSuccessPath },
      (data) => {
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        setCheckoutPlan(null);
        setError("Could not start checkout. Please try again.");
      },
      (requestError) => {
        setCheckoutPlan(null);
        setError(getApiErrorMessage(requestError, "Could not start checkout. Please try again."));
      }
    );
  };

  const changePlan = (key) => {
    const planName = planConfig?.[key]?.name || key;
    if (
      !window.confirm(
        `Are you sure you want to change your plan to ${planName}? You will be prorated appropriately.`
      )
    ) {
      return;
    }

    setError(null);
    setCheckoutPlan(key);
    subscriptionService.changePlan(
      key,
      billingCycle,
      () => {
        setCheckoutPlan(null);
        navigate("/account/subscription/?success=changed");
      },
      (requestError) => {
        setCheckoutPlan(null);
        setError(getApiErrorMessage(requestError, "Could not change your plan. Please try again."));
      }
    );
  };

  const selectPlan = (key) => {
    if (planConfig?.[key]?.disabled) return;

    if (isChangingPlan) {
      const currentBillingCycle =
        currentSubscription?.billing_cycle === "yearly" ? "yearly" : "monthly";
      if (key === planKey && billingCycle === currentBillingCycle) return;
      changePlan(key);
      return;
    }
    startCheckout(key);
  };

  const getCheckoutButtonLabel = (key) => {
    if (planConfig?.[key]?.disabled) return "Coming soon";
    if (checkoutPlan === key) {
      return isChangingPlan ? "Updating plan..." : "Starting checkout...";
    }
    if (isChangingPlan) {
      if (key === planKey) {
        return billingCycle === "yearly" ? "Switch to annual" : "Switch to monthly";
      }
      return `Switch to ${planConfig?.[key]?.name || key}`;
    }
    if (eligibleForTrial) return `Start ${trialDays}-day free trial`;
    return "Buy now";
  };

  const modalTitle = isChangingPlan ? "Change your plan" : "Choose your plan";
  const modalSubtitle = isChangingPlan
    ? "Upgrade, downgrade, or switch billing cycle. To cancel, use Cancel plan on the subscription page."
    : "Scale as you grow. Upgrade or downgrade anytime.";

  const planContent = (
    <div className={embedded ? "space-y-4" : "flex-1 overflow-y-auto p-6 space-y-6"}>
      {successMessage && (
        <div className="rounded-lg border border-green-500 border-opacity-50 bg-green-600 bg-opacity-15 px-4 py-3 text-sm text-green-400">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-500 border-opacity-50 bg-red-500 bg-opacity-15 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between sm:gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 min-w-0">
          {!isChangingPlan && (
            <span className="px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-md bg-green-600 text-white w-fit">
              Founding discount, applied automatically
            </span>
          )}
          <a
            href="mailto:fed@moonscroll.ai?subject=Enterprise%20Plan"
            className="text-xs sm:text-sm text-theme-text opacity-70 hover:opacity-100 underline underline-offset-2"
          >
            Enterprise needs? Get in touch
          </a>
        </div>
        <div className="flex shrink-0 self-start sm:self-auto rounded-lg border border-theme-secondary p-0.5">
          <button
            type="button"
            onClick={() => setBillingCycle("monthly")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              billingCycle === "monthly"
                ? "bg-theme-primary text-theme-button"
                : "text-theme-text opacity-70 hover:opacity-100"
            }`}
            style={billingCycle === "monthly" ? { backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" } : {}}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle("yearly")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              billingCycle === "yearly"
                ? "bg-theme-primary text-theme-button"
                : "text-theme-text opacity-70 hover:opacity-100"
            }`}
            style={billingCycle === "yearly" ? { backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" } : {}}
          >
            Annual
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${embedded ? "" : "md:grid-cols-2 lg:grid-cols-3"}`}>
        {planOrder.map((key) => {
          const plan = planConfig[key];
          const isDisabled = plan?.disabled;
          const currentBillingCycle = currentSubscription?.billing_cycle === "yearly" ? "yearly" : "monthly";
          const isCurrent = key === planKey && (!isChangingPlan || billingCycle === currentBillingCycle);
          const priceDisplay = getPlanPriceDisplay(plan, billingCycle);

          return (
            <div
              key={key}
              className={`relative flex flex-col rounded-lg border transition-all duration-200 overflow-hidden ${
                isCurrent
                  ? "border-theme-primary border-opacity-60 bg-theme-secondary bg-opacity-50 hover:border-theme-primary"
                  : "border-theme-secondary bg-theme-secondary bg-opacity-40 hover:border-theme-secondary"
              } ${isDisabled ? "opacity-75" : ""}`}
            >
              {isCurrent && (
                <div className="absolute top-3 right-3">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                    style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
                  >
                    Current plan
                  </span>
                </div>
              )}

              <div className={`flex flex-col flex-1 ${embedded ? "p-3" : "p-4"}`}>
                <h3 className="text-base font-bold text-theme-text">{plan?.name || key}</h3>
                <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                  {priceDisplay?.hasDiscount ? (
                    <>
                      <span className="text-2xl font-bold text-green-400">${priceDisplay.discounted}</span>
                      <span className="text-lg text-theme-text opacity-50 line-through">
                        ${priceDisplay.original}
                      </span>
                      <span className="text-theme-text opacity-70 text-sm">{priceDisplay.unit}</span>
                    </>
                  ) : priceDisplay ? (
                    <>
                      <span className="text-2xl font-bold text-theme-text">${priceDisplay.price}</span>
                      <span className="text-theme-text opacity-70 text-sm">{priceDisplay.unit}</span>
                    </>
                  ) : (
                    (() => {
                      const price = billingCycle === "yearly" ? plan?.price?.annual : plan?.price?.monthly;
                      const { main, unit } = formatPrice(price, billingCycle);
                      return (
                        <>
                          <span className="text-2xl font-bold text-theme-text">{main}</span>
                          {unit && <span className="text-theme-text opacity-70 text-sm">{unit}</span>}
                        </>
                      );
                    })()
                  )}
                </div>
                <p className="mt-1 text-sm text-theme-text opacity-70">{plan?.description || ""}</p>
                <ul className={`flex-1 ${embedded ? "mt-2 space-y-0.5" : "mt-3 space-y-1"}`}>
                  {buildPlanFeatures(key, planConfig).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-theme-text opacity-90">
                      <CheckIcon />
                      {item.type === "prefix" ? (
                        item.text
                      ) : item.description ? (
                        <>
                          <span
                            data-tooltip-id={`plan-feature-${key}-${i}`}
                            data-tooltip-content={item.description}
                            className="cursor-help underline decoration-dotted underline-offset-1"
                          >
                            {item.name}
                          </span>
                          <Tooltip id={`plan-feature-${key}-${i}`} place="top" positionStrategy="fixed" className="!max-w-[260px] !text-xs" />
                        </>
                      ) : (
                        item.name
                      )}
                    </li>
                  ))}
                </ul>

                {!embedded && (
                  <div className="mt-auto pt-4">
                    <hr className="border-0 h-px bg-theme-secondary opacity-60 mb-4" />
                    {(() => {
                      const limitItems = buildPlanLimits(key, planConfig);
                      return limitItems.length > 0 ? (
                        <div className="space-y-1.5 text-xs text-theme-text opacity-70 mb-4">
                          {limitItems.map((item) => (
                            <div key={item.key} className="flex items-center gap-2">
                              <span className="text-theme-primary opacity-80">{LIMIT_ICONS[item.key]}</span>
                              {item.text}
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                <div className={embedded ? "pt-3" : "pt-4"}>
                  {isDisabled ? (
                    <button
                      type="button"
                      disabled
                      className="w-full py-2 px-3 rounded text-sm font-medium border border-theme-secondary text-theme-text opacity-60 cursor-not-allowed"
                    >
                      Coming soon
                    </button>
                  ) : isCurrent ? (
                    <div className="py-2 px-3 rounded text-center text-sm text-theme-text opacity-70">
                      Current plan
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="w-full py-2 px-3 rounded text-sm font-medium bg-theme-primary text-theme-button hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: "var(--theme-primary)" }}
                      onClick={() => selectPlan(key)}
                      disabled={checkoutPlan !== null}
                    >
                      {getCheckoutButtonLabel(key)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {eligibleForTrial && !isChangingPlan && (
        <p className="text-center text-xs text-theme-text opacity-60">
          {trialDays}-day free trial, no payment upfront. Cancel anytime before your subscription starts.
        </p>
      )}

      {showSkipLink && onSkip && (
        <div className="text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-theme-text opacity-60 hover:opacity-100 underline"
          >
            I&apos;ll choose my plan later
          </button>
        </div>
      )}
    </div>
  );

  if (planConfig === null) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-12">
          <OrbitalLoader size={96} text="Loading plans..." />
        </div>
      );
    }
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme-secondary flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-theme-text">{modalTitle}</h2>
            <p className="mt-0.5 text-sm text-theme-text opacity-70">{modalSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/account/subscription/")}
            className="p-2 rounded-lg text-theme-text opacity-70 hover:opacity-100 hover:bg-theme-secondary transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center min-h-0">
          <OrbitalLoader size={128} text="Loading plans..." />
        </div>
      </div>
    );
  }

  if (embedded) {
    return planContent;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-theme-secondary flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-theme-text">{modalTitle}</h2>
          <p className="mt-0.5 text-sm text-theme-text opacity-70">{modalSubtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/account/subscription/")}
          className="p-2 rounded-lg text-theme-text opacity-70 hover:opacity-100 hover:bg-theme-secondary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {planContent}
    </div>
  );
};

export default SubscriptionSelect;
